import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Smartphone } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { BrandMark } from "@/components/BrandMark";
import { isNative, NATIVE_AUTH_RETURN_PATH, nativeDeepLink } from "@/lib/native";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useApp();
  const hasProcessed = useRef(false);
  const [appLink, setAppLink] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? match[1] : null;

    // Web bounce page for the Android app: hand the one-time session_id to the native shell instead of consuming it here.
    if (sessionId && !isNative() && window.location.pathname === NATIVE_AUTH_RETURN_PATH) {
      const link = nativeDeepLink(sessionId);
      setAppLink(link);
      window.location.href = link;
      return;
    }

    const run = async () => {
      if (!sessionId) {
        navigate("/auth");
        return;
      }
      try {
        const res = await api.post("/auth/session", { session_id: sessionId });
        setUser(res.data.user);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/home");
      } catch {
        navigate("/auth");
      }
    };
    run();
  }, [navigate, setUser]);

  return (
    <div className="h-full flex flex-col items-center justify-center bg-emerald-950 gap-5 px-8 text-center">
      <BrandMark size={64} />
      {appLink ? (
        <>
          <p className="text-sm text-emerald-100">Signed in. Returning you to the U&URS NUTRITIONIST app…</p>
          <a
            data-testid="open-in-app-btn"
            href={appLink}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-5 py-3 rounded-2xl"
          >
            <Smartphone className="w-5 h-5" /> Open the app
          </a>
        </>
      ) : (
        <div className="flex items-center gap-2 text-emerald-200">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm font-medium">Signing you in...</span>
        </div>
      )}
    </div>
  );
}
